import ReactCrop, { centerCrop, Crop, makeAspectCrop, PixelCrop } from "react-image-crop";
import { canvasPreview, Dictionary, Severity } from "../../Helpers";
import styles from "./ImageUploadCrop.module.scss";
import { FC, useCallback, useRef, useState } from "react";
import { Button } from "../Button";
import { useDropzone } from "react-dropzone";
import { classNames } from "../../Utilities";

interface ImageUploadCropProps {
    resource: (key: string, variables?: Dictionary<string | number>) => string;
    existingImages?: StorageFile[] | null;
    allowUseExisting?: boolean;
    disallowUpload?: boolean;
    onSave: (fileBase64: string) => void;
    onClose: () => void;
    targetWidth: number;
    targetHeight: number;
}

interface StorageFile {
    id: string;
    uri?: string | null;
}

function centerAspectCrop(
    mediaWidth: number,
    mediaHeight: number,
    aspect: number,
) {
    return centerCrop(
        makeAspectCrop(
            {
                unit: "%",
                width: 90,
            },
            aspect,
            mediaWidth,
            mediaHeight,
        ),
        mediaWidth,
        mediaHeight,
    );
}

export const ImageUploadCrop: FC<ImageUploadCropProps> = (props) => {
    const { resource } = props;

    const [stage, setStage] = useState<"source" | "choose" | "crop">("source");

    const imgRef = useRef<HTMLImageElement>(null);

    const [crop, setCrop] = useState<Crop>();
    const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
    const [imgSrc, setImgSrc] = useState<string>("");

    const onDrop = useCallback((acceptedFiles: File[]) => {
        fileToBase64(acceptedFiles[0]);
    }, []);

    const { getRootProps, getInputProps, isDragActive, isFocused, isDragAccept, isDragReject } = useDropzone({
        onDrop,
        maxFiles: 1,
        accept: { "image/*": [] }
    });

    const handleExistingImageClicked = async (image: StorageFile) => {
        const response = await fetch(image.uri!);
        const blob = await response.blob();
        fileToBase64(blob);
    };

    const fileToBase64 = (input: File | Blob) => {
        const reader = new FileReader();
        reader.addEventListener("load", () => {
            setImgSrc(reader.result?.toString() ?? "");
            setStage("crop");
        });
        reader.readAsDataURL(input);
    };

    const onImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
        const { width, height } = e.currentTarget;
        setCrop(centerAspectCrop(width, height, 1));
    };

    const saveCroppedImage = () => {
        if (!completedCrop || !imgRef.current) return;
        const result = canvasPreview(imgRef.current, completedCrop, props.targetWidth, props.targetHeight);

        // As Base64 string
        props.onSave(result);
    };

    const handleCropNextClicked = () => {
        if (!completedCrop || !completedCrop.width || !completedCrop.height) return;

        saveCroppedImage();
    };

    return (
        <>
            {stage === "source" && (
                <div>
                    {!props.disallowUpload && (
                        <div {...getRootProps({ className: classNames([styles.dropzone, isFocused && styles.focused, isDragAccept && styles.accepted, isDragReject && styles.rejected]) })}>
                            <input {...getInputProps()} />
                            {
                                isDragActive && isDragAccept
                                    ? <p>{resource("DropIt")}</p>
                                    : <p>{resource("DropImageOrClickToChoose")}</p>
                            }
                        </div>
                    )}
                    {!!props.allowUseExisting && (
                        <Button severity={Severity.Tertiary} onClick={() => setStage("choose")} className="asLink">
                            {props.disallowUpload
                                ? resource("SelectExistingImage")
                                : resource("OrSelectExistingImage")
                            }
                        </Button>
                    )}
                </div>
            )}

            {stage === "choose" && (
                <>
                    <h3>{resource("SelectExistingImage")}</h3>
                    <div className={styles.choose}>
                        <div className={styles.imageGrid}>
                            {props.existingImages?.map(image => (
                                <div
                                    className={styles.image}
                                    key={image.id}
                                    onClick={() => handleExistingImageClicked(image)}
                                >
                                    <img src={image.uri!} />
                                </div>
                            ))}
                        </div>
                    </div>
                </>
            )}

            {stage === "crop" && (
                <>
                    <h3>{resource("CropTheImage")}</h3>
                    <div className={styles.crop}>
                        {!!imgSrc && (
                            <div>
                                <ReactCrop
                                    crop={crop}
                                    onChange={(_, percentCrop) => setCrop(percentCrop)}
                                    onComplete={c => setCompletedCrop(c)}
                                    aspect={1}
                                    minWidth={200}
                                    minHeight={200}
                                >
                                    <img
                                        ref={imgRef}
                                        src={imgSrc}
                                        onLoad={onImageLoad}
                                    />
                                </ReactCrop>
                            </div>
                        )}
                        <div className={styles.buttons}>
                            <Button severity={Severity.Secondary} onClick={() => handleCropNextClicked()}>{resource("Next")}</Button>
                        </div>
                    </div>
                </>
            )}
        </>
    );
};